// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import { getBookingSessionToken, clearBookingSessionToken } from '@/auth/getBookingSessionToken'

// Build-time replaced by Bun's define plugin; in dev (HMR) this is undefined
declare const __API_BASE_URL__: string | undefined;
const API_BASE_URL = typeof __API_BASE_URL__ !== "undefined" ? __API_BASE_URL__ : "";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: async (headers) => {
    const token = await getBookingSessionToken();
    headers.set("Authorization", `Bearer ${token}`);
    headers.set("Accept", "application/json");
    return headers;
  },
});

// Every route on this slice requires a booking-session token (see BookingSessionsController). If the
// cached token expired mid-session (e.g. a booking-flow tab left open past its lifetime), a request
// comes back 401 — clear the cache and retry once with a freshly minted token before giving up.
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    clearBookingSessionToken();
    result = await rawBaseQuery(args, api, extraOptions);
  }
  return result;
};

// Separate API slice for the public booking flow, distinct from the staff-authenticated `services/api.ts`
// slice. Requests here carry a short-lived booking-session token (see `getBookingSessionToken.ts`)
// rather than a staff MSAL token, so a signed-in staff member browsing the public flow authenticates
// the same way an applicant does, and the flow works with no staff session at all.
export const publicApi = createApi({
  reducerPath: 'publicApi',
  baseQuery: baseQueryWithReauth,
  endpoints: builder => ({})
})
